<template>
  <div class="reg-shell">
    <RegistrationSidebar class="reg-shell__sidebar" :steps="store.sidebarSteps" @step-click="store.goToStep" />
    <RegistrationProgressBar class="reg-shell__progress" :label="store.breadcrumb" :percent="store.progress" />

    <div class="reg-shell__main">
      <RegistrationHeader :breadcrumb="store.breadcrumb" :show-sign-in="store.currentStepSequence === 1" />

      <main ref="paneRef" class="reg-shell__pane" tabindex="-1">
        <!-- the registration is already finished elsewhere (second tab, stale reload), so there is no step to return to -->
        <div
          v-if="store.finishedElsewhere"
          class="tw:flex tw:max-w-[520px] tw:flex-col tw:gap-4"
          data-testid="reg-finished-elsewhere">
          <RegistrationStepHeading
            :title="$t('registration.finished_elsewhere.title')"
            :lede="$t('registration.finished_elsewhere.lede')" />
          <LeadButton
            variant="primary"
            class="tw:w-fit"
            data-testid="reg-finished-elsewhere-dashboard"
            @click="goToDashboard">
            {{ $t('registration.finished_elsewhere.cta') }}
          </LeadButton>
        </div>

        <!-- a real form so Enter advances the wizard; textareas keep their newline behaviour -->
        <form v-else :id="STEP_FORM_ID" novalidate @submit.prevent="onContinue">
          <Transition name="reg-pane" mode="out-in">
            <!-- keyed on the step so switching remounts: each step registers its own submit handler on mount -->
            <component
              :is="stepComponent"
              :key="store.currentStep.key"
              v-model:allowNext="store.availableSteps[store.currentStep.key].canGoNext"
              :step-key="store.currentStep.key"
              :label="stepLabel" />
          </Transition>
        </form>
      </main>

      <RegistrationFooter
        v-if="store.showFooter"
        :form-id="STEP_FORM_ID"
        :next-label="store.nextLabel"
        :note="store.footerNote"
        :can-go-back="store.canGoBack"
        :can-go-next="store.canGoNext"
        :can-skip="store.canSkip"
        :is-submitting="store.isSubmitting"
        @back="store.back"
        @skip="store.skip" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type Component } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import LeadButton from '@/components/lead-design/LeadButton.vue';
import RegistrationFooter from '@/components/registration/subcomponents/RegistrationFooter.vue';
import RegistrationHeader from '@/components/registration/subcomponents/RegistrationHeader.vue';
import RegistrationProgressBar from '@/components/registration/subcomponents/RegistrationProgressBar.vue';
import RegistrationPlaceholderStep from '@/components/registration/subcomponents/RegistrationPlaceholderStep.vue';
import RegistrationSidebar from '@/components/registration/subcomponents/RegistrationSidebar.vue';
import RegistrationStepHeading from '@/components/registration/subcomponents/RegistrationStepHeading.vue';
import RegistrationAccountStep from '@/components/registration/steps/RegistrationAccountStep.vue';
import RegistrationProfileStep from '@/components/registration/steps/RegistrationProfileStep.vue';
import RegistrationCompanyStep from '@/components/registration/steps/RegistrationCompanyStep.vue';
import RegistrationCompletionStep from '@/components/registration/steps/RegistrationCompletionStep.vue';
import RegistrationGoalStep from '@/components/registration/steps/RegistrationGoalStep.vue';
import RegistrationPackageStep from '@/components/registration/steps/RegistrationPackageStep.vue';
import RegistrationVerifyEmailStep from '@/components/registration/steps/RegistrationVerifyEmailStep.vue';
import { isIntentionalReload } from '@/func/intentionalReload';
import translator from '@/locale/translator';
import { useRegistrationStore, type RegistrationStepKey } from '@/stores/registration';
import { useUserStore } from '@/stores/user';

/* ----------------------------------- STATE ---------------------------------- */
const store = useRegistrationStore();
const userStore = useUserStore();
const route = useRoute();
const router = useRouter();
const paneRef = ref<HTMLElement | null>(null);

/** ties the footer's primary action to the step form, so Enter and the click share one path */
const STEP_FORM_ID = 'registration-step-form';

/** steps without a component here fall back to the placeholder so the wizard stays walkable */
const STEP_COMPONENTS: Partial<Record<RegistrationStepKey, Component>> = {
  account: RegistrationAccountStep,
  verify_email: RegistrationVerifyEmailStep,
  profile: RegistrationProfileStep,
  company: RegistrationCompanyStep,
  goal: RegistrationGoalStep,
  package: RegistrationPackageStep,
  done: RegistrationCompletionStep,
};

/* ---------------------------------- COMPUTED -------------------------------- */
const stepComponent = computed(() => STEP_COMPONENTS[store.currentStep.key] ?? RegistrationPlaceholderStep);

const stepLabel = computed(
  () => store.sidebarSteps.find((step) => step.isCurrent)?.label ?? translator.get('registration.steps.done')
);

/* ---------------------------------- METHODS --------------------------------- */
/** the single way forward (Enter or button); guarded since some engines still submit implicitly via a disabled default button */
function onContinue() {
  if (!store.canGoNext || store.isSubmitting) return;
  store.next();
}

/** the one way out once there is no wizard step left to return to */
async function goToDashboard() {
  try {
    // the router guard reads registration_step from this store; a stale value would bounce back into the wizard
    await userStore.fetchCurrent();
  } catch {
    // best effort: still try to leave the stale wizard either way
  }
  router.push({ name: 'home' });
}

/** closing the tab discards everything, including a verified email, so it prompts; a reload can't be told apart */
function warnBeforeLeaving(event: BeforeUnloadEvent) {
  if (store.currentStepSequence < 3 || store.isComplete) return;
  // language changes and session recovery reload on purpose
  if (isIntentionalReload()) return;

  event.preventDefault();
  // returnValue is needed for older engines; set both so the prompt appears everywhere
  event.returnValue = '';
}

/* --------------------------------- LIFECYCLE -------------------------------- */
onMounted(() => {
  // the session decides how far a restored position may reach; storage only carries the answers.
  // the step marker and mobile-verified flag are already available, and each step refreshes its own section on entry
  store.rehydrate(
    userStore.isAuthenticated,
    Boolean(userStore.currentUser?.phone_verified_at),
    userStore.currentUser?.registration_step
  );
  // the backend's recorded step outranks the url and stored draft, so a stale step link can't walk a registrant backwards.
  // back from a Google or Microsoft sign-in (`?sso=`), the account step confirms the identity, whatever a stored draft says
  const requestedStep = typeof route.query.sso === 'string' ? 'account' : (route.query.step as string | undefined);
  store.restoreStepFromQuery(userStore.currentUser?.registration_step ?? requestedStep);
  store.startPersisting();

  window.addEventListener('beforeunload', warnBeforeLeaving);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', warnBeforeLeaving);
});

watch(
  () => store.currentStep.key,
  (step, previous) => {
    router.replace({ query: { ...route.query, step } });
    if (paneRef.value) paneRef.value.scrollTop = 0;

    // move focus into the new pane on a step change so keyboard/screen reader users notice it; skipped on the first run
    if (previous !== undefined) {
      nextTick(() => paneRef.value?.focus());
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.reg-shell {
  display: grid;
  grid-template-columns: auto 1fr;
  height: 100vh;
  background: #fff;

  &__progress {
    display: none;
  }

  &__main {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }

  &__pane {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 40px 72px 32px;

    // focused programmatically on a step change, so it shouldn't draw a focus ring
    &:focus {
      outline: none;
    }
  }
}

.reg-pane-enter-active,
.reg-pane-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.reg-pane-enter-from,
.reg-pane-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 767.98px) {
  .reg-shell {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;

    &__sidebar {
      display: none;
    }

    &__progress {
      display: flex;
    }

    &__pane {
      padding: 24px 16px;
    }
  }
}

@media (min-width: 768px) and (max-width: 1199.98px) {
  .reg-shell__pane {
    padding: 32px 32px 24px;
  }
}
</style>
